import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';
import { AG_GRID_LOCALE_ES } from '@ag-grid-community/locale';
import { AG_GRID_LOCALE_PT } from '@ag-grid-community/locale';
import { FileText } from 'lucide-react';
import { clientService } from '../../services/clientService';
import { workflowService } from '../../services/api/workflowService';
import { useAuth } from '../auth/AuthContext';
import { FORWARD_TRANSITIONS, WorkflowState } from '../../types/workflow';
import { humanizeFieldName } from '../../utils/fieldNames';
import InlineMenu, { InlineMenuItem } from '../common/InlineMenu';
import AlertModal from '../common/AlertModal';
import UploadingModal from '../common/UploadingModal';
import { logger } from '../../utils/logger';
import { formatTradeDate } from '../../utils/dateCalculations';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './TradeGrid.css';

interface ForwardMaturityGridProps {
  trades?: Record<string, any>[];
  onSelectionChanged?: (selectedRows: any[]) => void;
  selectedCounterparties?: Set<string>;
  selectedCurrencies?: Set<string>;
  selectedProducts?: Set<string>;
  selectedSettlementTypes?: Set<string>;
  clientId?: string;
  onRefresh?: () => void;
  scrollToNonContractSigned?: number;
}

/** Inline cell renderer for SI download button */
const SiDownloadButton: React.FC<{ clientId: string; storagePath: string; tradeNumber?: string }> = ({ clientId, storagePath, tradeNumber }) => {
  const [downloading, setDownloading] = useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (downloading) return;
    setDownloading(true);
    try {
      const urlResp = await clientService.getSettlementInstructionUrl(clientId, undefined, storagePath);
      if (urlResp.success && urlResp.data?.signed_url) {
        const a = document.createElement('a');
        a.href = urlResp.data.signed_url;
        a.download = '';
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (err) {
      logger.error('Failed to download SI:', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={downloading}
      title="Download SI"
      style={{
        background: 'none', border: 'none', cursor: downloading ? 'wait' : 'pointer',
        color: 'var(--accent-blue)', padding: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <FileText size={14} />
    </button>
  );
};

const ForwardMaturityGrid: React.FC<ForwardMaturityGridProps> = ({
  trades = [],
  onSelectionChanged,
  selectedCounterparties = new Set(),
  selectedCurrencies = new Set(),
  selectedProducts = new Set(),
  selectedSettlementTypes = new Set(),
  clientId,
  onRefresh,
  scrollToNonContractSigned,
}) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const gridRef = useRef<AgGridReact>(null);

  const agGridLocale = useMemo(() => {
    const lang = i18n.language?.substring(0, 2);
    if (lang === 'es') return AG_GRID_LOCALE_ES;
    if (lang === 'pt') return AG_GRID_LOCALE_PT;
    return undefined;
  }, [i18n.language]);

  // Right-click context menu state
  const [showInlineMenu, setShowInlineMenu] = useState(false);
  const [inlineMenuPosition, setInlineMenuPosition] = useState({ x: 0, y: 0 });
  const [selectedTradeForMenu, setSelectedTradeForMenu] = useState<any>(null);
  const [processingWorkflow, setProcessingWorkflow] = useState(false);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [workflowAlert, setWorkflowAlert] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'warning' | 'error' | 'info' | 'success';
  }>({ isOpen: false, title: '', message: '', type: 'info' });

  // Disputed-reason dialog state (ContractSigned → Final when expiryState is disputed)
  const [disputedReasonDialog, setDisputedReasonDialog] = useState<{
    isOpen: boolean;
    trade: any;
    targetState: WorkflowState | null;
  }>({ isOpen: false, trade: null, targetState: null });
  const [disputedReason, setDisputedReason] = useState('');

  // Load demo mode setting
  useEffect(() => {
    const loadWorkflowConfig = async () => {
      if (!clientId) return;
      try {
        const response = await workflowService.getWorkflowConfig(clientId);
        if (response.success && response.data) {
          setIsDemoMode(response.data.demoMode || false);
        }
      } catch (error) {
        logger.warn('Failed to load workflow config for demo mode check:', error);
      }
    };
    loadWorkflowConfig();
  }, [clientId]);

  // Get valid transitions for a trade (forward trades only on this page)
  const getValidTransitions = useCallback((trade: any): WorkflowState[] => {
    const currentState = trade.workflowState as WorkflowState || 'Draft';
    if (currentState === 'Final' || currentState === 'Cancelled' || currentState === 'Unwound') {
      return [];
    }
    return FORWARD_TRANSITIONS
      .filter(tr => tr.fromState === currentState)
      .map(tr => tr.toState);
  }, []);

  // Handle workflow transition
  const handleWorkflowTransition = useCallback(async (trade: any, targetState: WorkflowState, comment?: string) => {
    if (!clientId) return;

    setProcessingWorkflow(true);
    let pendingAlert: typeof workflowAlert | null = null;

    try {
      const tradeOrgId = trade.organizationId as string;
      const response = await workflowService.requestTransition(tradeOrgId, trade.id, targetState, comment);

      if (response.success) {
        if (response.data?.pendingApproval) {
          pendingAlert = {
            isOpen: true,
            title: t('workflow.messages.approvalRequired'),
            message: t('workflow.messages.pendingApproval', {
              state: targetState,
              required: response.data.requiredApprovers
            }),
            type: 'info'
          };
        } else {
          logger.info(`Trade ${trade.TradeNumber} transitioned to ${targetState}`);
        }
        onRefresh?.();
      } else {
        throw new Error(response.message || 'Failed to process workflow transition');
      }
    } catch (error: any) {
      logger.error('Workflow transition failed:', error);
      // KAN-199: Detect structured transition block error
      if (error?.detail?.blocked) {
        const missingFields = (error.detail.missingFields || []) as string[];
        const fieldNames = missingFields.map(f => t(`tradeFields.${f}`, humanizeFieldName(f))).join(', ');
        pendingAlert = {
          isOpen: true,
          title: t('trades.transitionBlock.modalTitle', 'Transition Blocked'),
          message: t('trades.transitionBlock.modalMessage',
            'The following fields must be completed before this transition can proceed: {{fields}}. Please edit the trade to fill in the required fields, then try the transition again.',
            { fields: fieldNames }
          ),
          type: 'warning'
        };
      } else {
        const errorMsg = error instanceof Error ? error.message : (error?.message || error?.detail || 'Unknown error');
        // KAN-205: Same-day guard — show translated message
        const isSameDayGuard = typeof errorMsg === 'string' && errorMsg.includes('previous business day');
        // KAN-222: Delete matched trade guard — show translated message
        const isDeleteMatchedGuard = typeof errorMsg === 'string' && errorMsg.includes('active confirmation match');
        pendingAlert = {
          isOpen: true,
          title: t('common.error'),
          message: isSameDayGuard
            ? t('workflow.messages.sameDayGuardError')
            : isDeleteMatchedGuard
            ? t('workflow.messages.deleteMatchedGuardError')
            : t('workflow.messages.transitionError', { error: errorMsg }),
          type: 'error'
        };
      }
    } finally {
      setProcessingWorkflow(false);
      if (pendingAlert) {
        setWorkflowAlert(pendingAlert);
      }
    }
  }, [clientId, onRefresh, t]);

  // Handle workflow approval
  const handleWorkflowApproval = useCallback(async (trade: any) => {
    if (!clientId) return;

    setProcessingWorkflow(true);
    let pendingAlert: typeof workflowAlert | null = null;

    try {
      const tradeOrgId = trade.organizationId as string;
      const response = await workflowService.approveTransition(tradeOrgId, trade.id);

      if (response.success) {
        if (response.data?.pendingApproval) {
          pendingAlert = {
            isOpen: true,
            title: t('workflow.messages.approvalRecordedTitle'),
            message: t('workflow.messages.approvalRecorded', {
              current: response.data.currentApprovers,
              required: response.data.requiredApprovers
            }),
            type: 'success'
          };
        } else {
          pendingAlert = {
            isOpen: true,
            title: t('common.success'),
            message: t('workflow.messages.transitionComplete', {
              state: trade.pendingApproval?.targetState || 'completed'
            }),
            type: 'success'
          };
          logger.info(`Trade ${trade.TradeNumber} approval completed`);
        }
        onRefresh?.();
      } else {
        throw new Error(response.message || 'Failed to approve transition');
      }
    } catch (error) {
      logger.error('Workflow approval failed:', error);
      pendingAlert = {
        isOpen: true,
        title: t('common.error'),
        message: t('workflow.messages.approvalError', {
          error: error instanceof Error ? error.message : 'Unknown error'
        }),
        type: 'error'
      };
    } finally {
      setProcessingWorkflow(false);
      if (pendingAlert) {
        setWorkflowAlert(pendingAlert);
      }
    }
  }, [clientId, onRefresh, t]);

  // Right-click context menu handler
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const cellElement = target.closest('.ag-cell');

      if (cellElement) {
        e.preventDefault();
        e.stopPropagation();

        const rowElement = cellElement.closest('[role="row"]');
        if (rowElement && gridRef.current?.api) {
          const rowIndex = rowElement.getAttribute('row-index');
          if (rowIndex) {
            const rowNode = gridRef.current.api.getDisplayedRowAtIndex(parseInt(rowIndex));
            if (rowNode && !rowNode.data?._isGroupHeader) {
              // Only select if row is selectable (ContractSigned/Final)
              if (rowNode.selectable) {
                rowNode.setSelected(true, true);
              }
              setSelectedTradeForMenu(rowNode.data);
              setInlineMenuPosition({ x: e.clientX, y: e.clientY });
              setShowInlineMenu(true);
            }
          }
        }
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [trades]);

  // Apply filters (KAN-244: handle separator row and update its count)
  const filteredTrades = useMemo(() => {
    const filtered = trades.filter(trade => {
      // Skip separator row during filtering — we'll re-insert it below
      if (trade._isGroupHeader) return false;

      const counterpartyMatch = selectedCounterparties.size === 0 || selectedCounterparties.has(trade.CounterpartyName);
      const currencyMatch = selectedCurrencies.size === 0 || selectedCurrencies.has(trade.SettlementCurrency);
      const productMatch = selectedProducts.size === 0 || (
        (selectedProducts.has('Forward') && !trade.IsAverageRateForward && !trade.IsForwardStarting) ||
        (selectedProducts.has('Forward Asiático') && trade.IsAverageRateForward) ||
        (selectedProducts.has('Forward Starting') && trade.IsForwardStarting)
      );
      const settlementTypeMatch = selectedSettlementTypes.size === 0 || selectedSettlementTypes.has(trade.SettlementType);
      return counterpartyMatch && currencyMatch && productMatch && settlementTypeMatch;
    });

    // Re-insert separator row if there are non-contract-signed trades after filtering
    const contractSigned = filtered.filter(t => t.workflowState === 'ContractSigned' || t.workflowState === 'Final');
    const nonContractSigned = filtered.filter(t => t.workflowState === 'Booked' || t.workflowState === 'Confirmed');

    if (nonContractSigned.length === 0) {
      return contractSigned;
    }

    const separatorRow = {
      _isGroupHeader: true,
      _nonContractSignedCount: nonContractSigned.length,
      id: '__separator__',
    };

    return [...contractSigned, separatorRow, ...nonContractSigned];
  }, [trades, selectedCounterparties, selectedCurrencies, selectedProducts, selectedSettlementTypes]);

  // Auto-select filtered rows when filters change
  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      const hasActiveFilters = selectedCounterparties.size > 0 || selectedCurrencies.size > 0 || selectedProducts.size > 0 || selectedSettlementTypes.size > 0;

      setTimeout(() => {
        if (gridRef.current && gridRef.current.api) {
          if (hasActiveFilters) {
            gridRef.current.api.selectAll();
          } else {
            gridRef.current.api.deselectAll();
          }
          const selectedRows = gridRef.current.api.getSelectedRows();
          if (onSelectionChanged) {
            onSelectionChanged(selectedRows);
          }
        }
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCounterparties, selectedCurrencies, selectedProducts, selectedSettlementTypes]);

  // KAN-244: Scroll to non-contract-signed section when warning icon is clicked
  useEffect(() => {
    if (scrollToNonContractSigned && scrollToNonContractSigned > 0 && gridRef.current?.api) {
      const separatorIndex = filteredTrades.findIndex(t => t._isGroupHeader);
      if (separatorIndex >= 0) {
        gridRef.current.api.ensureIndexVisible(separatorIndex, 'top');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToNonContractSigned]);

  // Show OurParty column only when trades span multiple entities
  const isMultiEntity = useMemo(() => {
    const parties = new Set((trades || []).filter(t => !t._isGroupHeader).map(t => t.OurParty).filter(Boolean));
    return parties.size > 1;
  }, [trades]);

  // 22-column definitions per spec §7.2.5
  const columnDefs = useMemo<ColDef[]>(() => [
    // 1. Trade Number
    {
      headerName: t('forwardMaturity.columns.tradeNumber'),
      field: 'TradeNumber',
      width: 120,
      sortable: true,
      filter: true,
      sort: 'asc' as const,
      sortIndex: 0
    },
    // 2. Workflow State
    {
      headerName: t('forwardMaturity.columns.workflowState'),
      field: 'workflowState',
      width: 140,
      sortable: true,
      filter: true,
      cellRenderer: (params: any): React.ReactElement => {
        const blockedAuto = params.data?.blockedAutoTransition;
        const text = params.value || 'Draft';
        if (!blockedAuto) {
          return React.createElement('span', {}, text);
        }
        const missingNames = (blockedAuto.missingFields || []).map((f: string) => t(`tradeFields.${f}`, humanizeFieldName(f))).join(', ');
        const tooltip = t('trades.transitionBlock.blockedIndicatorTooltip',
          'Auto-transition {{transition}} blocked by rule "{{ruleName}}". Missing fields: {{fields}}. Fill in these fields to allow the trade to advance.',
          { transition: blockedAuto.transition, ruleName: blockedAuto.ruleName, fields: missingNames }
        );
        return React.createElement('span', {}, [
          text,
          React.createElement('span', {
            key: 'blocked',
            title: tooltip,
            style: { marginLeft: '4px', color: '#f59e0b', cursor: 'help' }
          }, '\u26A0')
        ]);
      }
    },
    // 3. Product
    {
      headerName: t('forwardMaturity.columns.product'),
      field: 'ProductLabel',
      width: 140,
      sortable: true,
      filter: true
    },
    // 4. Our Entity (hidden for single-entity clients)
    {
      headerName: t('forwardMaturity.columns.ourParty'),
      field: 'OurParty',
      width: 140,
      sortable: true,
      filter: true,
      hide: !isMultiEntity,
    },
    // 5. Bank Trade Number
    {
      headerName: t('forwardMaturity.columns.bankTradeNumber'),
      field: 'bankTradeNumber',
      width: 140,
      sortable: true,
      filter: true
    },
    // 5b. Legacy References
    {
      headerName: t('forwardMaturity.columns.legacyReference1'),
      field: 'LegacyReference1',
      width: 120,
      sortable: true,
      filter: true
    },
    {
      headerName: t('forwardMaturity.columns.legacyReference2'),
      field: 'LegacyReference2',
      width: 120,
      sortable: true,
      filter: true
    },
    // 6. Counterparty
    {
      headerName: t('forwardMaturity.columns.counterparty'),
      field: 'CounterpartyName',
      width: 160,
      sortable: true,
      filter: true
    },
    // 7. Trade Date
    {
      headerName: t('forwardMaturity.columns.tradeDate'),
      field: 'TradeDate',
      width: 130,
      sortable: true,
      filter: true,
      valueFormatter: (params) => formatTradeDate(params.value)
    },
    // 8. Maturity Date
    {
      headerName: t('forwardMaturity.columns.maturityDate'),
      field: 'MaturityDate',
      width: 140,
      sortable: true,
      filter: true,
      valueFormatter: (params) => formatTradeDate(params.value)
    },
    // 6. Direction
    {
      headerName: t('forwardMaturity.columns.direction'),
      field: 'Direction',
      width: 90,
      sortable: true,
      filter: true
    },
    // 7. CCY1
    {
      headerName: t('forwardMaturity.columns.currency1'),
      field: 'Currency1',
      width: 80,
      sortable: true,
      filter: true
    },
    // 8. Amount 1
    {
      headerName: t('forwardMaturity.columns.amount'),
      field: 'QuantityCurrency1',
      width: 140,
      sortable: true,
      filter: true,
      valueFormatter: (params) => {
        const value = params.value || 0;
        return value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      }
    },
    // 9. Price
    {
      headerName: t('forwardMaturity.columns.price'),
      field: 'Price',
      width: 100,
      sortable: true,
      filter: true,
      valueFormatter: (params) => {
        if (params.data?.IsForwardStarting && (!params.value || params.value === 0)) {
          return t('tradeBooking.forwardStarting.toBeFixed');
        }
        const value = params.value || 0;
        return value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 6
        });
      }
    },
    // 10. CCY2
    {
      headerName: t('forwardMaturity.columns.currency2'),
      field: 'Currency2',
      width: 80,
      sortable: true,
      filter: true
    },
    // 11. Settlement Type
    {
      headerName: t('forwardMaturity.columns.settlementType'),
      field: 'SettlementType',
      width: 140,
      sortable: true,
      filter: true
    },
    // 12. Settlement Currency
    {
      headerName: t('forwardMaturity.columns.settlementCurrency'),
      field: 'SettlementCurrency',
      width: 140,
      sortable: true,
      filter: true
    },
    // 13. Fixing Reference
    {
      headerName: t('forwardMaturity.columns.fixingReference'),
      field: 'FixingReference',
      width: 140,
      sortable: true,
      filter: true
    },
    // 14. Fixing Offset
    {
      headerName: t('forwardMaturity.columns.fixingOffset'),
      field: 'DisplayFixingOffset',
      width: 120,
      sortable: true,
      filter: true,
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined || params.value === '' || params.value === 'N/A') {
          return params.value ?? '';
        }
        const num = Number(params.value);
        return isNaN(num) ? String(params.value) : String(num);
      }
    },
    // 15. Fixing Price
    {
      headerName: t('forwardMaturity.columns.fixingPrice'),
      field: 'DisplayFixingPrice',
      width: 120,
      sortable: true,
      filter: true,
      valueFormatter: (params) => {
        if (params.data?.SettlementType === 'Entrega Física') return 'N/A';
        if (params.value == null) return '';
        return params.value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 6
        });
      }
    },
    // 16. Sett. Conversion Rate
    {
      headerName: t('forwardMaturity.columns.settlementExchangeRate'),
      field: 'SettlementPrice',
      width: 140,
      sortable: true,
      filter: true,
      valueFormatter: (params) => {
        if (params.data?.SettlementType === 'Entrega Física') return 'N/A';
        if (params.value == null) return '';
        return params.value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 6
        });
      }
    },
    // 17. We Pay Currency
    {
      headerName: t('forwardMaturity.columns.wePayCurrency'),
      field: 'WePayCurrency',
      width: 100,
      sortable: true,
      filter: true
    },
    // 18. We Pay Amount
    {
      headerName: t('forwardMaturity.columns.wePayAmount'),
      field: 'WePayAmount',
      width: 140,
      sortable: true,
      filter: true,
      valueFormatter: (params) => {
        if (params.value == null) return '';
        return params.value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      }
    },
    // 19. We Receive Currency
    {
      headerName: t('forwardMaturity.columns.weReceiveCurrency'),
      field: 'WeReceiveCurrency',
      width: 120,
      sortable: true,
      filter: true
    },
    // 20. We Receive Amount
    {
      headerName: t('forwardMaturity.columns.weReceiveAmount'),
      field: 'WeReceiveAmount',
      width: 140,
      sortable: true,
      filter: true,
      valueFormatter: (params) => {
        if (params.value == null) return '';
        return params.value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      }
    },
    // 22. Expiry State
    {
      headerName: t('forwardMaturity.columns.expiryState'),
      field: 'expiryState',
      width: 130,
      sortable: true,
      filter: true,
      valueGetter: (params: any) => {
        const state = params.data?.expiryState;
        if (!state) return '';
        const keyMap: Record<string, string> = {
          pending: 'forwardMaturity.columns.expiryStatePending',
          received: 'forwardMaturity.columns.expiryStateReceived',
          agreed: 'forwardMaturity.columns.expiryStateAgreed',
          disputed: 'forwardMaturity.columns.expiryStateDisputed',
          notified: 'forwardMaturity.columns.expiryStateNotified',
        };
        return keyMap[state] ? t(keyMap[state]) : state;
      },
      cellStyle: (params: any) => {
        const state = params.data?.expiryState;
        if (state === 'agreed') return { color: '#28a745', textAlign: 'center', fontWeight: 'bold' };
        if (state === 'disputed') return { color: '#dc3545', textAlign: 'center', fontWeight: 'bold' };
        if (state === 'received') return { color: '#ffc107', textAlign: 'center', fontWeight: 'bold' };
        if (state === 'notified') return { color: 'var(--accent-blue)', textAlign: 'center', fontWeight: 'bold' };
        return { textAlign: 'center', color: '#b3b3b3' };
      }
    },
    // 23. Settlement Instruction download
    {
      headerName: t('forwardMaturity.columns.settlementLetter'),
      field: 'settlementInstructionStoragePath',
      width: 80,
      sortable: false,
      filter: false,
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
      cellRenderer: (params: any) => {
        const path = params.data?.settlementInstructionStoragePath;
        if (!path || !clientId) return null;
        return <SiDownloadButton clientId={clientId} storagePath={path} tradeNumber={params.data?.TradeNumber} />;
      },
    }
  ], [t, clientId, isMultiEntity]);

  // Build context menu items
  const buildMenuItems = useCallback((): (InlineMenuItem | 'separator')[] => {
    if (!selectedTradeForMenu) return [];

    const menuItems: (InlineMenuItem | 'separator')[] = [];
    const hasBackOfficeRole = ['client_admin', 'client_bo_user', 'client_fo_bo_user'].includes(user?.role || '');

    if (!hasBackOfficeRole) {
      return [{
        label: t('grid.contextMenu.noAccessToActions', 'View only - no actions available'),
        action: () => setShowInlineMenu(false),
        disabled: true,
        className: 'info-text',
      }];
    }

    // Internal trades are read-only
    if (selectedTradeForMenu.isInternalTrade) {
      return [{
        label: t('grid.contextMenu.internalTradeReadOnly'),
        action: () => setShowInlineMenu(false),
        disabled: true,
        className: 'info-text',
      }];
    }

    // KAN-244: Non-contract-signed trades — no expiry actions
    const wfState = selectedTradeForMenu.workflowState;
    if (wfState === 'Booked' || wfState === 'Confirmed') {
      return [{
        label: t('forwardMaturity.nonContractSigned.noActions', 'Trade not in Contract Signed — no expiry actions available'),
        action: () => setShowInlineMenu(false),
        disabled: true,
        className: 'info-text',
      }];
    }

    let validTransitions = getValidTransitions(selectedTradeForMenu);
    const hasPendingApproval = !!selectedTradeForMenu.pendingApproval;
    const currentState = selectedTradeForMenu.workflowState || 'Draft';

    // Forward Expiries context: ContractSigned↔Final (and revert to Confirmed)
    if (currentState === 'ContractSigned') {
      validTransitions = validTransitions.filter(state => state === 'Final' || state === 'Confirmed');
    } else {
      validTransitions = [];
    }

    // If trade has pending approval, show approve option
    if (hasPendingApproval) {
      const pendingTo = selectedTradeForMenu.pendingApproval?.targetState;
      const currentApprovals = selectedTradeForMenu.pendingApproval?.currentApprovals || [];
      const userAlreadyApproved = currentApprovals.some(
        (a: { userId: string }) => a.userId === user?.id
      );
      const requestedBy = selectedTradeForMenu.pendingApproval?.requestedBy;
      const userRequestedTransition = requestedBy === user?.id;
      const canApprove = isDemoMode || (!userAlreadyApproved && !userRequestedTransition);

      // Only show approve if the pending target is relevant to this page
      const approvalRelevantToContext = pendingTo === 'ContractSigned' || pendingTo === 'Confirmed' || pendingTo === 'Final';

      if (canApprove && approvalRelevantToContext) {
        menuItems.push({
          label: t('workflow.actions.approve', { state: pendingTo }),
          action: () => {
            setShowInlineMenu(false);
            handleWorkflowApproval(selectedTradeForMenu);
          },
          disabled: processingWorkflow
        });
      }
    }

    // Show available transitions or "no transitions available" message
    if (validTransitions.length === 0 && !hasPendingApproval) {
      menuItems.push({
        label: t('workflow.messages.noTransitionsAvailable'),
        action: () => {},
        disabled: true,
        className: 'info-text'
      });
    } else {
      if (menuItems.length > 0 && validTransitions.length > 0) {
        menuItems.push('separator');
      }
      validTransitions.forEach((targetState) => {
        const isRevert = (currentState === 'ContractSigned' && targetState === 'Confirmed');

        menuItems.push({
          label: isRevert
            ? t('workflow.actions.revertTo', { state: targetState })
            : t('workflow.actions.advanceTo', { state: targetState }),
          action: () => {
            setShowInlineMenu(false);
            // If advancing to Final and expiry is disputed, require a reason
            if (targetState === 'Final' && selectedTradeForMenu.expiryState === 'disputed') {
              setDisputedReason('');
              setDisputedReasonDialog({ isOpen: true, trade: selectedTradeForMenu, targetState });
            } else {
              handleWorkflowTransition(selectedTradeForMenu, targetState);
            }
          },
          disabled: processingWorkflow || hasPendingApproval
        });
      });
    }

    return menuItems;
  }, [selectedTradeForMenu, user, isDemoMode, processingWorkflow, getValidTransitions, handleWorkflowTransition, handleWorkflowApproval, t]);

  return (
    <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {trades.length === 0 ? (
        <div className="empty-state" style={{ flex: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#b3b3b3' }}>
          {t('forwardMaturity.noTradesForDate')}
        </div>
      ) : (
        <div className="ag-theme-alpine-dark trade-grid" style={{ flex: '1', minHeight: 0, height: '100%' }}>
          <AgGridReact
            ref={gridRef}
            rowData={filteredTrades}
            columnDefs={columnDefs}
            localeText={agGridLocale}
            pagination={true}
            paginationPageSize={50}
            suppressMovableColumns={false}
            domLayout="normal"
            rowSelection="multiple"
            isRowSelectable={(node) => {
              if (node.data?._isGroupHeader) return false;
              const state = node.data?.workflowState;
              return state === 'ContractSigned' || state === 'Final';
            }}
            getRowStyle={(params) => {
              if (params.data?._isGroupHeader) {
                return {
                  backgroundColor: 'rgba(245, 158, 11, 0.15)',
                  fontStyle: 'italic',
                  fontWeight: 600,
                  borderTop: '1px solid rgba(245, 158, 11, 0.3)',
                  borderBottom: '1px solid rgba(245, 158, 11, 0.3)',
                };
              }
              const state = params.data?.workflowState;
              if (state === 'Booked' || state === 'Confirmed') {
                return { backgroundColor: 'rgba(245, 158, 11, 0.08)' };
              }
              return undefined;
            }}
            postSortRows={(params) => {
              // KAN-244: After any sort, re-group rows so ContractSigned/Final
              // appear first, then the separator, then Booked/Confirmed.
              const nodes = params.nodes;
              const contractSigned: any[] = [];
              const separator: any[] = [];
              const nonContractSigned: any[] = [];

              for (const node of nodes) {
                if (node.data?._isGroupHeader) {
                  separator.push(node);
                } else {
                  const state = node.data?.workflowState;
                  if (state === 'Booked' || state === 'Confirmed') {
                    nonContractSigned.push(node);
                  } else {
                    contractSigned.push(node);
                  }
                }
              }

              // Rebuild: clear and re-add in correct order
              nodes.length = 0;
              nodes.push(...contractSigned, ...separator, ...nonContractSigned);
            }}
            isFullWidthRow={(params) => params.rowNode.data?._isGroupHeader === true}
            fullWidthCellRenderer={(params: any) => {
              const count = params.data?._nonContractSignedCount || 0;
              return React.createElement('div', {
                style: {
                  padding: '6px 12px',
                  color: '#f59e0b',
                  fontSize: '13px',
                  fontWeight: 600,
                  fontStyle: 'italic',
                  display: 'flex',
                  alignItems: 'center',
                  height: '100%',
                }
              }, t('forwardMaturity.nonContractSigned.separator', { count }));
            }}
            onSelectionChanged={(event) => {
              if (onSelectionChanged) {
                const selectedRows = event.api.getSelectedRows();
                onSelectionChanged(selectedRows);
              }
            }}
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: true,
              minWidth: 80
            }}
            sortingOrder={['asc', 'desc']}
            animateRows={true}
          />
        </div>
      )}

      {/* Right-click context menu */}
      {showInlineMenu && selectedTradeForMenu && (
        <InlineMenu
          position={inlineMenuPosition}
          onClose={() => setShowInlineMenu(false)}
          items={buildMenuItems()}
        />
      )}

      {/* Workflow processing overlay */}
      <UploadingModal isOpen={processingWorkflow} message={t('workflow.messages.processing')} />

      {/* Workflow alert modal */}
      <AlertModal
        isOpen={workflowAlert.isOpen}
        onClose={() => setWorkflowAlert(prev => ({ ...prev, isOpen: false }))}
        title={workflowAlert.title}
        message={workflowAlert.message}
        type={workflowAlert.type}
      />

      {/* Disputed-reason dialog: requires explanation when advancing disputed trade to Final */}
      {disputedReasonDialog.isOpen && (
        <div className="alert-modal-backdrop" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setDisputedReasonDialog({ isOpen: false, trade: null, targetState: null });
          }
        }}>
          <div className="alert-modal alert-modal-warning">
            <div className="alert-modal-header">
              <span className="alert-modal-icon">{'\u26A0\uFE0F'}</span>
              <h3 className="alert-modal-title">{t('workflow.messages.disputedReasonTitle', 'Expiry Status: Disputed')}</h3>
              <button
                className="alert-modal-close"
                onClick={() => setDisputedReasonDialog({ isOpen: false, trade: null, targetState: null })}
              >
                &times;
              </button>
            </div>
            <div className="alert-modal-content">
              <p>{t('workflow.messages.disputedReasonMessage', 'This trade has a disputed expiry status. Please provide a reason for advancing to Final.')}</p>
              <textarea
                value={disputedReason}
                onChange={(e) => setDisputedReason(e.target.value)}
                placeholder={t('workflow.messages.disputedReasonPlaceholder', 'e.g. Difference confirmed as rounding, approved by...')}
                rows={3}
                style={{
                  width: '100%',
                  marginTop: '0.75rem',
                  padding: '0.625rem',
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  color: 'var(--text-primary)',
                  fontSize: '0.9rem',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
                autoFocus
              />
            </div>
            <div className="alert-modal-actions">
              <button
                className="alert-modal-button secondary"
                onClick={() => setDisputedReasonDialog({ isOpen: false, trade: null, targetState: null })}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                className="alert-modal-button primary"
                disabled={!disputedReason.trim()}
                style={{ opacity: disputedReason.trim() ? 1 : 0.5, cursor: disputedReason.trim() ? 'pointer' : 'not-allowed' }}
                onClick={() => {
                  const { trade, targetState } = disputedReasonDialog;
                  setDisputedReasonDialog({ isOpen: false, trade: null, targetState: null });
                  if (trade && targetState) {
                    handleWorkflowTransition(trade, targetState, disputedReason.trim());
                  }
                }}
              >
                {t('workflow.actions.advanceTo', { state: 'Final' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForwardMaturityGrid;
